import React, { useEffect, useState, useRef } from "react";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/hooks/useAuth";

import {
  UPSERT_USER_PROFILE_MUTATION,
  SAVE_ONBOARDING_STATE_MUTATION,
} from "@/graphql/mutations/onboarding";
import { UPDATE_USER_MUTATION } from "@/graphql/mutations";
import { useOnboardingAutosave } from "./standard/hooks/useOnboardingAutosave";
import Loader from "@/components/views/Loader";
import Logo from "@/components/views/Logo";

// Step views
import BasicsStep from "./standard/steps/BasicsStep";
import StyleStep from "./standard/steps/StyleStep";

const steps = ["Basics", "Style"] as const;

const StandardOnboardingWizard: React.FC = () => {
  const navigate = useNavigate();
  const { user, refetchUser } = useAuth();
  const isMountedRef = useRef(true);
  const [step, setStep] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const { scheduleSave } = useOnboardingAutosave(600);

  // Use user data from useAuth instead of redundant query
  const current = user;
  const loading = !user; // Consider loading if no user data yet

  // Cleanup effect
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Handle navigation when user is onboarded
  useEffect(() => {
    console.log("🚀 Navigation effect triggered:", { 
      loading, 
      onboarded: current?.onboarded, 
      isMounted: isMountedRef.current,
      user: current 
    });
    
    if (loading || !isMountedRef.current) return;

    if (current?.onboarded) {
      console.log("✅ User is onboarded, navigating to /home via useEffect");
      // Use setTimeout to prevent navigation during render
      setTimeout(() => {
        if (isMountedRef.current) {
          navigate("/home", { replace: true });
        }
      }, 0);
      return;
    }
  }, [loading, current?.onboarded, navigate]);

  // Initialize step from saved state (only run once when data loads)
  useEffect(() => {
    if (loading || step !== null || !isMountedRef.current) return;

    const savedStep = current?.onboardingState?.currentStep;
    setStep(typeof savedStep === "number" ? savedStep : 0);
  }, [loading, current?.onboardingState?.currentStep, step]);

  const [upsertUserProfile] = useMutation(UPSERT_USER_PROFILE_MUTATION);

  const [saveState] = useMutation(SAVE_ONBOARDING_STATE_MUTATION);

  const saveCurrentStep = async (stepIndex: number) => {
    try {
      await saveState({
        variables: { state: { currentStep: stepIndex }, merge: true },
      });
    } catch (e) {
      console.error("Failed to save onboarding step", e);
    }
  };
  const [updateUser] = useMutation(UPDATE_USER_MUTATION);

  if (loading || step === null) return <Loader />;

  const next = () => {
    const nextStep = Math.min(step + 1, steps.length - 1);
    setStep(nextStep);
    saveCurrentStep(nextStep);
  };

  const back = () => {
    const prevStep = Math.max(step - 1, 0);
    setStep(prevStep);
    saveCurrentStep(prevStep);
  };

  // Handlers per step
  const handleBasicsSubmit = async (values: any) => {
    setSaving(true);
    try {
      await upsertUserProfile({ variables: { input: values } });
      scheduleSave({ step: { basics: values } });
      next();
    } catch (e) {
      // Show inline error in step via return false
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const handleStyleSubmit = async (_values: any) => {
    setSaving(true);
    try {
      console.log("🚀 Starting onboarding completion...");
      console.log("👤 Current user before update:", current);
      
      // The wardrobe items are already created in the StyleStep itself.
      // Here, we just mark the user as onboarded and finish.
      const updateResult = await updateUser({
        variables: { fieldName: "onboarded", fieldValue: "true" },
      });
      
      console.log("✅ Update user result:", updateResult);
      
      // Refetch user data to update the useAuth context
      const refetchResult = await refetchUser();
      console.log("🔄 Refetch user result:", refetchResult);
      
      // Navigate to home - the useEffect will handle this once user data updates
      // But we also navigate directly as a fallback
      console.log("🏠 Navigating to /home...");
      navigate("/home", { replace: true });
    } catch (e) {
      console.error("❌ Error in handleStyleSubmit:", e);
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const stepProps = {
    saving,
    autosave: scheduleSave,
    data: current,
  } as const;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-pink-50 text-gray-900 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900 dark:text-white flex flex-col justify-center items-center p-4 font-light">
      <div className="w-full max-w-2xl mx-auto bg-white dark:bg-neutral-900/50 rounded-lg p-8 space-y-8 backdrop-blur-sm">
        <div className="flex justify-center">
          <Logo />
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center space-x-2">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center space-x-2">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-sm transition-all duration-300 ${
                    i <= step
                      ? "bg-black dark:bg-white text-white dark:text-black"
                      : "border-2 border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {i < step ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <span>{i + 1}</span>
                  )}
                </div>
                <span
                  className={`font-medium tracking-wider uppercase ${
                    i === step
                      ? "text-black dark:text-white"
                      : "text-gray-400 dark:text-gray-500"
                  }`}
                >
                  {s}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div className="w-16 h-px bg-gray-300 dark:bg-gray-700" />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <div className="pt-4">
          {step === 0 && (
            <BasicsStep
              {...stepProps}
              initial={current?.userProfile}
              onSubmit={handleBasicsSubmit}
            />
          )}
          {step === 1 && (
            <StyleStep
              saving={saving}
              onSubmit={handleStyleSubmit}
              onBack={back}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default StandardOnboardingWizard;
